import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { MatPaginator, MatSort, MatTableDataSource, PageEvent } from '@iota/iota-ui-components';
import moment from 'moment';
import { exportArrayToExcel } from '../../utils';
import { Subscription } from 'rxjs';
import { ProgressiveRenderService } from '../../services/progressive-render.service';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../constants-and-enums/pagination';
import { ISO_DATE_FORMAT } from '../../utils/date.util';
import { rememberPageSize, resolvePageSize } from '../../utils/page-size.util';

export interface SmartTableHeader<TColumnKey extends string = string> {
    key: TColumnKey;
    label?: string;
    tooltip?: string;
    hidden?: boolean;
    searchable?: boolean;
    // Used by the server-paginated table to drive column sorting.
    sortable?: boolean;
    sortingKey?: string;
}

export interface SmartTableCell {
    data?: string;
    prefix?: string;
    suffix?: string;

    isDate?: boolean;

    bold?: boolean;
    crossedOut?: boolean;

    // When a routerLink is provided, the data is set to a link
    routerLink?: string;
    // When a template is provided, data is hidden but searchable
    htmlTemplate?: string;
    // Can be used to store the raw (or extra) data like booleans, Dates etc.
    rawData?: any;
    // Overrides data and rawData as the sort key
    sortValue?: string | number;
}

export type SmartTableRow<TColumnKey extends string = string> = Record<TColumnKey, SmartTableCell>;

export interface SmartTableHeaderAndData<TColumnKey extends string = string> {
    headers?: readonly SmartTableHeader<TColumnKey>[];
    rows?: SmartTableRow<TColumnKey>[];
}

/**
 * Column keys of a header list declared with `as const`. Use it to type the matching rows so that
 * adding, renaming or removing a header becomes a compile error in the row mapper.
 */
export type SmartTableColumnKeys<THeaders extends readonly SmartTableHeader[]> = THeaders[number]['key'];

/**
 * Builds headers and rows as one table and infers the column keys from the header list, so a row
 * that misses a column or uses an unknown key does not compile. Prefer this over building the
 * object literal by hand, which cannot check headers and rows against each other.
 */
export function createSmartTable<
    const THeaders extends readonly SmartTableHeader[],
    TRow extends SmartTableRow<SmartTableColumnKeys<THeaders>>
>(table: {
    headers: THeaders;
    // The intersection maps every key without a header to `never`, which rejects unknown columns.
    rows: (TRow & Record<Exclude<keyof TRow, SmartTableColumnKeys<THeaders>>, never>)[];
}): SmartTableHeaderAndData<SmartTableColumnKeys<THeaders>> {
    return table;
}

export function statusBadgeCell(label: string, themeClasses: string): SmartTableCell {
    return { data: label, htmlTemplate: `<span class="${themeClasses} text-xs font-bold px-2 py-1 rounded-full">${label}</span>` };
}

@Component({
    standalone: false,
    selector: 'app-smart-table',
    templateUrl: './smart-table.component.html',
    styleUrls: ['./smart-table.component.scss']
})
export class SmartTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() tableHeaderAndData: SmartTableHeaderAndData;

    @Input() firstColumnIsSticky = true;
    @Input() emptyTableText = $localize`No entries found.`;
    @Input() loading = false;

    @Input() actionButtonsTemplate: any;
    @Input() customContentTemplate: any;
    @Input() logoTemplate: any;

    // Pagination options
    @Input() allowPagination = true;
    @Input() pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
    @Input() defaultPageSize = DEFAULT_PAGE_SIZE;

    @Input() minimalPadding = false;
    @Input() minimalRowPadding = false;
    @Input() zebraRows = true;

    @Output() doubleClickAction = new EventEmitter<SmartTableRow>();
    @Input() defaultSorting: { active: string; direction: 'asc' | 'desc' };

    dataSource = new MatTableDataSource<SmartTableRow>([]);
    headerKeys: string[] = [];

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    private renderSubscription: Subscription;
    useProgressiveRendering = true;

    rendering = false;

    constructor(private progressiveRenderService: ProgressiveRenderService) {}

    ngOnInit(): void {
        this.defaultPageSize = resolvePageSize(this.pageSizeOptions, this.defaultPageSize);
        this.updateTable();
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        if (this.defaultSorting) {
            this.sort.active = this.defaultSorting.active;
            this.sort.direction = this.defaultSorting.direction;
            this.sort.sortChange.emit({ active: this.sort.active, direction: this.sort.direction });
        }
        this.updateTable();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.tableHeaderAndData?.currentValue) {
            this.updateTable();
        }
    }

    updateTable() {
        const newHeaderAndData = this.tableHeaderAndData;
        this.headerKeys = newHeaderAndData?.headers?.filter((header) => !header?.hidden).map((header) => header.key) ?? [];

        if (this.logoTemplate) {
            this.headerKeys = ['logo-template', ...this.headerKeys];
        }

        if (this.actionButtonsTemplate) {
            this.headerKeys = [...this.headerKeys, 'action-buttons'];
        }

        if (this.renderSubscription) {
            this.renderSubscription.unsubscribe();
        }

        if (this.useProgressiveRendering && newHeaderAndData?.rows?.length > 500) {
            if (this.renderSubscription) {
                this.renderSubscription.unsubscribe();
            }

            this.rendering = true;
            this.dataSource.data = [];

            this.renderSubscription = this.progressiveRenderService.progressivelyRender(newHeaderAndData.rows).subscribe({
                next: (rows) => {
                    this.dataSource.data = rows;
                },
                complete: () => {
                    this.rendering = false;
                }
            });
        } else {
            this.dataSource.data = newHeaderAndData?.rows || [];
        }

        if (this.allowPagination) {
            this.dataSource.paginator = this.paginator;
        }

        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (data, attribute) => {
            const sortValue = data?.[attribute]?.sortValue;
            if (sortValue !== undefined) {
                return typeof sortValue === 'string' ? sortValue.toLowerCase() : sortValue;
            }

            const rawData = data?.[attribute]?.rawData;
            const formattedData = data?.[attribute]?.data;
            const property = rawData ?? formattedData;

            if (data?.[attribute]?.isDate) {
                const isoFormattedDate = moment(property, ISO_DATE_FORMAT, true);
                if (isoFormattedDate.isValid()) {
                    return isoFormattedDate.toDate().getTime();
                }

                const isoFormattedDateTime = moment(property, moment.ISO_8601, true);
                if (isoFormattedDateTime.isValid()) {
                    return isoFormattedDateTime.toDate().getTime();
                }

                const germanFormattedDate = moment(property, 'DD.MM.YYYY', true);
                if (germanFormattedDate.isValid()) {
                    return germanFormattedDate.toDate().getTime();
                }
            }

            const number = parseInt(property);
            if (!isNaN(number)) {
                return number;
            }

            return typeof property === 'string' ? property.toLowerCase() : property;
        };
    }

    onPage(event: PageEvent): void {
        rememberPageSize(event.pageSize);
    }

    doubleClickActionHandler(row: SmartTableRow) {
        if (this.doubleClickAction) {
            this.doubleClickAction.emit(row);
        }
    }

    ngOnDestroy() {
        if (this.renderSubscription) {
            this.renderSubscription.unsubscribe();
        }
    }
}

export function downloadSmartTableContent(headerAndData: SmartTableHeaderAndData) {
    const exportArray = [];
    const keyToLabelMap: Record<string, string> = {};

    const visibleHeaders = headerAndData?.headers?.filter((header) => !header?.hidden);
    visibleHeaders.forEach((header) => (keyToLabelMap[header?.key] = header?.label));

    const allRows = headerAndData?.rows;

    if (!allRows || allRows?.length === 0) {
        const exportObj = {};
        visibleHeaders.forEach((header) => {
            const label = keyToLabelMap?.[header?.key];
            exportObj[label] = '';
        });
        exportArray.push(exportObj);
    } else {
        allRows.forEach((row) => {
            const exportObj = {};
            visibleHeaders.forEach((header) => {
                const label = keyToLabelMap?.[header?.key];
                exportObj[label] = row?.[header?.key]?.data;
            });
            exportArray.push(exportObj);
        });
    }

    exportArrayToExcel(exportArray);
}
